import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wrench, Calendar, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { persistSessionToken } from '@/lib/authFetch';
import { invalidateSubscription } from '@/hooks/useSubscription';
import { invalidateFeatureFlags } from '@/hooks/useFeatureFlags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already authenticated
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          navigate('/');
        }
      } catch (error) {
        // Not authenticated, stay on login page
      }
    };
    checkAuth();
  }, [navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || 'Login failed');
        setLoading(false);
        return;
      }

      const data = await response.json();
      // Persist the token so Safari/iOS can authenticate via
      // Authorization header even if its ITP dropped the cookie.
      // See lib/authFetch.js for the global interceptor that uses it.
      persistSessionToken(data.session_token);
      // iter241 — SPA login does NOT reload the page, so module-level
      // caches from the previous session must be dropped or the new user
      // inherits the old user's subscription/flags (pro-feature leak).
      invalidateSubscription();
      invalidateFeatureFlags();
      toast.success(`Welcome back, ${data.user.name}!`);
      navigate(data.user.role === 'mechanic' ? '/my-workshop' : '/');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1727413434026-0f8314c037d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtZWNoYW5pYyUyMHdvcmtzaG9wJTIwY2FyJTIwcmVwYWlyfGVufDB8fHx8MTc2ODk1Nzk4MXww&ixlib=rb-4.1.0&q=85')"
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 to-slate-900/80" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-['Chivo'] font-bold text-4xl mb-4">
            AutoShopFlow
          </h2>
          <p className="text-lg text-slate-200 max-w-md">
            Your complete digital platform for mechanical operations.
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
            <div className="flex items-center justify-center mb-8">
              <div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center">
                <Wrench className="h-8 w-8 text-white" />
              </div>
            </div>
            
            <h1 className="font-['Chivo'] font-bold text-3xl text-slate-900 text-center mb-2">
              Welcome Back
            </h1>
            <p className="text-slate-500 text-center mb-8">
              Sign in to access your workshop dashboard
            </p>

            {/* Email/Password Form */}
            <form onSubmit={handleEmailLogin} data-testid="email-login-form">
                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
                      placeholder="your.email@workshop.com"
                      required
                      className="h-12"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                      inputMode="email"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-slate-700 font-medium mb-2 block">
                      Password
                    </Label>
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="h-12"
                      data-testid="password-input"
                    />
                    <label className="mt-2 flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showPassword}
                        onChange={(e) => setShowPassword(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        data-testid="show-password-checkbox"
                      />
                      Show password
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md transition-all active:scale-95 shadow-sm hover:shadow-md"
                  data-testid="email-login-button"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-xs text-slate-400 text-center">
                Secure authentication powered by Emergent
              </p>
            </div>
            
            {/* Customer Booking Link */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="text-center">
                <p className="text-sm text-slate-500 mb-3">Looking to book a service?</p>
                <Link 
                  to="/book" 
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg text-green-700 font-medium transition-colors"
                  data-testid="customer-booking-link"
                >
                  <Calendar className="h-4 w-4" />
                  Customer Booking Page
                  <ExternalLink className="h-3 w-3" />
                </Link>
                <p className="text-xs text-slate-400 mt-2">
                  Share this link: <span className="font-mono text-blue-600">/book</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}